import type {
  ForestMarketPayload,
  NumberTrainPayload,
  PatternBridgePayload,
  PatternToken
} from "@/content/math";
import type { ActiveHintStage } from "@/game/feedback";

import { defineCurriculumVoiceCue, type CurriculumVoiceCue } from "./cue";

export const MATH_PATTERN_TOKEN_LABELS: Readonly<
  Record<PatternToken, string>
> = {
  acorn: "橡果",
  flower: "花朵",
  leaf: "叶子",
  mushroom: "蘑菇",
  shell: "贝壳",
  star: "星星"
};

const MARKET_ITEM_LABELS: Readonly<Record<string, string>> = {
  apple: "苹果",
  berry: "莓果",
  carrot: "胡萝卜",
  corn: "玉米",
  mushroom: "蘑菇",
  pear: "梨"
};

const MARKET_ITEM_UNITS: Readonly<Record<string, string>> = {
  apple: "个",
  berry: "颗",
  carrot: "根",
  corn: "根",
  mushroom: "朵",
  pear: "个"
};

export function getMarketItemLabel(itemId: string): string {
  return MARKET_ITEM_LABELS[itemId] ?? "物品";
}

export function getMarketItemUnit(itemId: string): string {
  return MARKET_ITEM_UNITS[itemId] ?? "个";
}

export function getNumberTrainHintCue(
  payload: NumberTrainPayload,
  stage: ActiveHintStage
): CurriculumVoiceCue {
  const pathPrefix = "/audio/math/feedback/number-train-hint-";

  switch (stage) {
    case "gentle-hint":
      return payload.mode === "match"
        ? defineCurriculumVoiceCue(
            pathPrefix + "gentle-match.ogg",
            "让手指陪星星排队：一颗一颗数，再找一样多的车厢。"
          )
        : defineCurriculumVoiceCue(
            pathPrefix + "gentle-compare.ogg",
            "每节车厢都数一数，再比比谁多、谁少。"
          );
    case "reduced-choices":
      return defineCurriculumVoiceCue(
        pathPrefix + "reduced-choices.ogg",
        "只剩两节车厢啦。把星星一颗颗对着比。"
      );
    case "replay-demonstration":
      return defineCurriculumVoiceCue(
        pathPrefix + "replay-demonstration.ogg",
        "点点从第一节开始数。跟着亮起来的车厢，再数一次。"
      );
  }
}

export function getNumberTrainRetryCue(
  payload: NumberTrainPayload
): CurriculumVoiceCue {
  if (payload.mode === "match") {
    return defineCurriculumVoiceCue(
      "/audio/math/feedback/number-train-retry-match.ogg",
      "数量还差一点。把站台和车厢里的星星一颗颗对着数。"
    );
  }

  const relation = payload.relation === "most" ? "最多" : "最少";
  return defineCurriculumVoiceCue(
    "/audio/math/feedback/number-train-retry-" + payload.relation + ".ogg",
    "再给每节车厢数一次，找找星星" + relation + "的那一节。"
  );
}

export function getForestMarketHintCue(
  payload: ForestMarketPayload,
  stage: ActiveHintStage
): CurriculumVoiceCue {
  switch (stage) {
    case "gentle-hint":
      return defineCurriculumVoiceCue(
        "/audio/math/feedback/forest-market-hint-gentle-" +
          payload.itemId +
          ".ogg",
        "篮子里原来有一些" +
          getMarketItemLabel(payload.itemId) +
          "。先数清，再看看这次是放进还是拿走。"
      );
    case "reduced-choices":
      return defineCurriculumVoiceCue(
        "/audio/math/feedback/forest-market-hint-reduced-choices.ogg",
        "只剩两个篮子啦。把里面的东西一个一个数清楚。"
      );
    case "replay-demonstration":
      return defineCurriculumVoiceCue(
        "/audio/math/feedback/forest-market-hint-replay-demonstration.ogg",
        "从原来的数量出发：放进就接着数，拿走就往回数。"
      );
  }
}

export function getForestMarketRetryCue(
  payload: ForestMarketPayload
): CurriculumVoiceCue {
  return defineCurriculumVoiceCue(
    "/audio/math/feedback/forest-market-retry-" +
      payload.itemId +
      "-" +
      payload.operation +
      "-" +
      String(payload.changeCount) +
      ".ogg",
    "还差一点。再数数" +
      getMarketItemLabel(payload.itemId) +
      "，看看" +
      (payload.operation === "add" ? "放进" : "拿走") +
      String(payload.changeCount) +
      getMarketItemUnit(payload.itemId) +
      "以后还剩多少。"
  );
}

export function getPatternHintCue(
  payload: PatternBridgePayload,
  stage: ActiveHintStage
): CurriculumVoiceCue {
  const mode = payload.mode;

  if (stage === "reduced-choices") {
    return defineCurriculumVoiceCue(
      "/audio/math/feedback/pattern-" + mode + "-hint-reduced-choices.ogg",
      mode === "sequence"
        ? "只剩两个图案可以选。顺着小规律再念一遍。"
        : "只剩两个格子啦。跟着箭头，从小船出发。"
    );
  }

  if (stage === "replay-demonstration") {
    return defineCurriculumVoiceCue(
      "/audio/math/feedback/pattern-" +
        mode +
        "-hint-replay-demonstration.ogg",
      mode === "sequence"
        ? "点点从第一个图案开始念，亮起来的图案会接着排队。"
        : "点点陪小船再走一遍。按箭头，走到亮起来的格子。"
    );
  }

  if (payload.mode === "sequence") {
    return defineCurriculumVoiceCue(
      "/audio/math/feedback/pattern-sequence-hint-gentle-" +
        payload.repeatUnit.join("-") +
        ".ogg",
      payload.repeatUnit
        .map((token) => MATH_PATTERN_TOKEN_LABELS[token])
        .join("、") + "在轮流排队。念一遍，空位该轮到谁？"
    );
  }

  return defineCurriculumVoiceCue(
    "/audio/math/feedback/pattern-route-hint-gentle.ogg",
    "让小手跟着箭头，从小船出发，一步一步走。"
  );
}

export function getPatternRetryCue(
  payload: PatternBridgePayload
): CurriculumVoiceCue {
  if (payload.mode === "route") {
    return defineCurriculumVoiceCue(
      "/audio/math/feedback/pattern-route-retry.ogg",
      "终点还差一格。让小船回到起点，再跟着箭头走一次。"
    );
  }

  const visibleTokens = payload.sequence.flatMap((token) =>
    token === null ? [] : [token]
  );
  return defineCurriculumVoiceCue(
    "/audio/math/feedback/pattern-sequence-retry-" +
      visibleTokens.join("-") +
      ".ogg",
    "这个图案还没接上。把" +
      visibleTokens
        .map((token) => MATH_PATTERN_TOKEN_LABELS[token])
        .join("、") +
      "轻轻念一遍，再选一个图案。"
  );
}
